import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import useApi from "./useApi";
import {
  saveWorkflowRunValues,
  submitWorkflowRunForm,
  WorkflowRunSaveResult,
  WorkflowRunSubmitResult,
} from "../services/workflowService";
import {
  WorkflowFormSubmitRequestDto,
  WorkflowInstanceSaveValuesRequestDto,
  WorkflowInstanceValueInputDto,
  WorkflowValidationIssueDto,
} from "../types/workflowDto";

/** Long enough that a typed word is one save, short enough that a closed tab loses little. */
export const WORKFLOW_AUTOSAVE_DELAY_MS = 800;

export type WorkflowFormSaveStatus = "idle" | "saving" | "failed" | "conflict";

export type WorkflowFormSubmitOutcome =
  /** `rowVersionTag` is the case's tag after the submit, for a write that follows straight on. */
  | { kind: "submitted"; rowVersionTag?: string }
  /** `fieldErrors` is keyed by canvas field id; `messages` are the issues no field claims. */
  | { kind: "invalid"; fieldErrors: Record<string, string>; messages: string[] }
  | { kind: "conflict" }
  | { kind: "forbidden" }
  | { kind: "not-found" }
  /** No answer from the server (network, 5xx) — useApi has already raised its alert. */
  | { kind: "failed" };

export interface WorkflowFormSaveTarget {
  instanceId: number;
  stageId: number;
  /** The tag the case was loaded with. Every write hands back a new one, which the hook keeps. */
  rowVersionTag: string;
}

const toInvalidOutcome = (issues: WorkflowValidationIssueDto[]): WorkflowFormSubmitOutcome => {
  const fieldErrors: Record<string, string> = {};
  const messages: string[] = [];
  issues.forEach((issue) => {
    if (issue.fieldId === null || issue.fieldId === undefined) messages.push(issue.message);
    else fieldErrors[String(issue.fieldId)] ??= issue.message;
  });
  return { kind: "invalid", fieldErrors, messages };
};

/**
 * Persists a form stage of a real case: a debounced autosave on every change, an explicit Save
 * draft, and Continue (form/submit, which commits and advances in one call).
 *
 * Writes run one at a time, each with the latest `rowVersionTag` — an autosave still in flight
 * when Continue is clicked would otherwise make the submit carry a stale tag and 409 against the
 * case's own save. A failed save never touches the caller's values; it only reports `failed`.
 */
const useWorkflowFormSave = (target: WorkflowFormSaveTarget | null) => {
  const { executeApi } = useApi();
  const instanceId = target?.instanceId ?? null;
  const stageId = target?.stageId ?? null;
  const loadedTag = target?.rowVersionTag ?? null;

  const [saveStatus, setSaveStatus] = useState<WorkflowFormSaveStatus>("idle");
  const [submitting, setSubmitting] = useState(false);
  const tagRef = useRef(loadedTag ?? "");
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const submittingRef = useRef(false);
  // Kept until the server gives a definite answer, so a Continue retried after a dropped
  // connection replays the first submit instead of advancing twice.
  const submitKeyRef = useRef<string | null>(null);

  // A freshly loaded case (or a new stage of it) starts clean, from the tag it was loaded with.
  useEffect(() => {
    tagRef.current = loadedTag ?? "";
    submitKeyRef.current = null;
    setSaveStatus("idle");
  }, [instanceId, stageId, loadedTag]);

  useEffect(() => () => clearTimeout(timerRef.current), [instanceId, stageId]);

  const enqueue = useCallback(<T,>(operation: () => Promise<T>): Promise<T> => {
    const next = queueRef.current.then(operation, operation);
    queueRef.current = next.catch(() => undefined);
    return next;
  }, []);

  const save = useCallback(
    (values: WorkflowInstanceValueInputDto[]): Promise<boolean> =>
      enqueue(async () => {
        if (!instanceId || !stageId || values.length === 0) return true;
        setSaveStatus("saving");
        const result = await executeApi<WorkflowInstanceSaveValuesRequestDto, WorkflowRunSaveResult>(
          saveWorkflowRunValues,
          { instanceId, stageId, rowVersionTag: tagRef.current, values },
        );
        if (!result) {
          setSaveStatus("failed");
          return false;
        }
        if (result.kind === "conflict") {
          setSaveStatus("conflict");
          return false;
        }
        tagRef.current = result.rowVersionTag;
        setSaveStatus("idle");
        return true;
      }),
    [enqueue, executeApi, instanceId, stageId],
  );

  /** Debounced — call on every change; only the last values inside the window are sent. */
  const queueSave = useCallback(
    (values: WorkflowInstanceValueInputDto[]) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        void save(values);
      }, WORKFLOW_AUTOSAVE_DELAY_MS);
    },
    [save],
  );

  /** Save draft / Retry — sends now and drops any autosave still waiting. */
  const saveNow = useCallback(
    (values: WorkflowInstanceValueInputDto[]) => {
      clearTimeout(timerRef.current);
      return save(values);
    },
    [save],
  );

  const submit = useCallback(
    async (values: WorkflowInstanceValueInputDto[]): Promise<WorkflowFormSubmitOutcome | null> => {
      // A second click while the first is still in flight is ignored, not queued.
      if (!instanceId || !stageId || submittingRef.current) return null;
      clearTimeout(timerRef.current);
      submittingRef.current = true;
      setSubmitting(true);
      submitKeyRef.current ??= uuidv4();
      const idempotencyKey = submitKeyRef.current;
      try {
        return await enqueue(async (): Promise<WorkflowFormSubmitOutcome> => {
          const result = await executeApi<WorkflowFormSubmitRequestDto, WorkflowRunSubmitResult>(
            submitWorkflowRunForm,
            { instanceId, stageId, rowVersionTag: tagRef.current, idempotencyKey, values },
          );
          if (!result) return { kind: "failed" };
          submitKeyRef.current = null;
          if (result.kind === "submitted") {
            if (result.result.rowVersionTag) tagRef.current = result.result.rowVersionTag;
            return { kind: "submitted", rowVersionTag: result.result.rowVersionTag ?? undefined };
          }
          if (result.kind === "invalid") return toInvalidOutcome(result.issues);
          if (result.kind === "conflict") setSaveStatus("conflict");
          return { kind: result.kind };
        });
      } finally {
        submittingRef.current = false;
        setSubmitting(false);
      }
    },
    [enqueue, executeApi, instanceId, stageId],
  );

  return { saveStatus, submitting, queueSave, saveNow, submit };
};

export default useWorkflowFormSave;
